// 編輯器底圖的畫面（快照 / 即時 / 灰底）與狀態列。繪圖層由呼叫端疊在同一個容器上。
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { useBackdrop } from './useBackdrop';

export function Backdrop({ b, onAspect }: { b: ReturnType<typeof useBackdrop>; onAspect: (ar: number) => void }) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.srcObject = b.stream;
    if (b.stream) void v.play?.()?.catch(() => undefined);
  }, [b.stream]);

  const onFrame = () => {
    const v = videoRef.current;
    if (!v || v.videoWidth <= 0 || v.videoHeight <= 0) return;
    onAspect(v.videoWidth / v.videoHeight);
    b.markPlaying();
  };

  let status: ReactNode = null;
  if (b.state === 'loading') status = <span>{t('zed.snapshot_loading')}</span>;
  else if (b.state === 'connecting') status = <span>{t('zed.live_connecting')}</span>;
  else if (b.state === 'none' || b.state === 'snapshot' || b.state === 'unavailable')
    status = (
      <>
        <span>{b.state === 'none' ? t('zed.no_snapshot') : b.state === 'unavailable' ? t('zed.live_unavailable') : t('zed.snapshot_shown')}</span>
        <button type="button" className="btn btn-sm" onPointerDown={(e) => e.stopPropagation()} onClick={b.startLive} data-testid="zed_live">
          {b.state === 'unavailable' ? t('common.retry') : t('zed.view_live')}
        </button>
      </>
    );

  return (
    <>
      {b.snapshotUrl && b.state !== 'playing' && (
        <img
          className="zed-backdrop"
          src={b.snapshotUrl}
          alt=""
          data-testid="zed_snapshot"
          onLoad={(e) => {
            const im = e.currentTarget;
            if (im.naturalWidth > 0 && im.naturalHeight > 0) onAspect(im.naturalWidth / im.naturalHeight);
          }}
        />
      )}
      {b.liveRequested && (
        <video ref={videoRef} className="zed-backdrop" autoPlay playsInline muted onLoadedMetadata={onFrame} onResize={onFrame} onPlaying={onFrame} data-testid="zed_video" />
      )}
      {status && (
        <div className="zed-status" role="status" data-testid="zed_backdrop" data-state={b.state}>
          {status}
        </div>
      )}
    </>
  );
}
